import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

/* ── Reusable Bootstrap numbered pagination ── */
function Pagination({ page, totalPages, onPageChange, disabled }) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <nav
      aria-label="Page navigation"
      className="mt-3 d-flex justify-content-center"
    >
      <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
        <li className={`page-item ${page <= 1 || disabled ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page - 1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        </li>

        {getPages().map((p, i) =>
          p === "..." ? (
            <li key={`ellipsis-${i}`} className="page-item disabled">
              <span className="page-link border-0 bg-transparent">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button
                className="page-link rounded-3"
                onClick={() => onPageChange(p)}
                disabled={disabled}
              >
                {p}
              </button>
            </li>
          ),
        )}

        <li
          className={`page-item ${page >= totalPages || disabled ? "disabled" : ""}`}
        >
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page + 1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function AdminCommissionsLedger() {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  /* filter state — single source of truth */
  const [userIdInput, setUserIdInput] = useState("");
  const [appliedUserId, setAppliedUserId] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const adminToken = localStorage.getItem("adminToken");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / limit)),
    [total, limit],
  );

  const fetchLedger = async (nextPage = 1, userId = appliedUserId) => {
    setLoading(true);
    setErrorMsg("");

    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const qs = new URLSearchParams({
        page: String(nextPage),
        limit: String(limit),
      });
      if (userId.trim()) qs.set("userId", userId.trim());

      const res = await fetch(
        `${API_BASE}/Ledger/GetCommissionsLedger?${qs.toString()}`,
        { headers: { Authorization: adminToken } },
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const result = await res.json();
      setCommissions(result?.commissions || []);
      setTotal(result?.total || 0);
      setPage(result?.page || nextPage);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to fetch commissions ledger.");
      setCommissions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  /* initial load */
  useEffect(() => {
    fetchLedger(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedUserId(userIdInput);
    setPage(1);
    fetchLedger(1, userIdInput);
  };

  const handleClear = () => {
    setUserIdInput("");
    setAppliedUserId("");
    setPage(1);
    fetchLedger(1, "");
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchLedger(p, appliedUserId);
  };

  return (
    <>
      <Header />

      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              {/* Header Row */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">
                    Commissions Ledger ({total})
                  </h3>
                  <div className="text-muted">
                    Commission Credits / Levels / Payment Reference
                  </div>
                </div>

                <div
                  className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                  onClick={() => fetchLedger(page, appliedUserId)}
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-dark" />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                  )}
                </div>
              </div>

              {/* ── Professional Filter Card ── */}
              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-person me-1 text-muted" />
                        User ID
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={userIdInput}
                          onChange={(e) => setUserIdInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search by User ID…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading || (!userIdInput && !appliedUserId)}
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              ) : null}

              {/* Table */}
              <div className="card shadow-sm rounded-4">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>No.</th>
                          <th>Details</th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="2" className="text-center py-5">
                              <span className="spinner-border spinner-border-sm" />
                            </td>
                          </tr>
                        ) : commissions.length === 0 ? (
                          <tr>
                            <td
                              colSpan="2"
                              className="text-center py-5 text-muted"
                            >
                              No commission records found.
                            </td>
                          </tr>
                        ) : (
                          commissions.map((c, idx) => (
                            <tr key={c.commissionId || idx}>
                              <td className="fw-semibold">
                                {(page - 1) * limit + idx + 1}.
                              </td>
                              <td>
                                <div className="lh-sm my-2">
                                  <div>
                                    <span className="text-muted">
                                      Commission ID
                                    </span>{" "}
                                    —{" "}
                                    <span className="fw-semibold">
                                      {c.commissionId}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">User ID</span>{" "}
                                    — {c.userId}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      From User
                                    </span>{" "}
                                    — {c.fromUserId}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Payment ID
                                    </span>{" "}
                                    — {c.paymentId}
                                  </div>
                                  <div>
                                    <span className="text-muted">Level</span> —{" "}
                                    <span className="badge bg-light text-dark border">
                                      {c.level}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">Amount</span> —
                                    ₹{c.amount}
                                  </div>
                                  <div>
                                    <span className="text-muted">Status</span> —{" "}
                                    <span
                                      className={`badge ${String(c.status).toLowerCase() === "credited" ? "bg-success" : "bg-secondary"}`}
                                    >
                                      {c.status}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Created At
                                    </span>{" "}
                                    — {formatDate(c.createdAt)}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                <div className="text-muted small">
                  Showing {commissions.length ? (page - 1) * limit + 1 : 0}–
                  {Math.min(page * limit, total)} of {total} records
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminCommissionsLedger;
